// SLA contra o WootDash — substitui o n8n (que por baixo só lia/gravava Chatwoot).
// Diferenças reais em relação ao modelo antigo, mantidas de propósito (nossa arquitetura
// está correta): tempos em SEGUNDOS (não minutos — a UI converte pra exibição), perfil
// carrega BusinessHoursOnly (não existia antes), fluxo é a entidade real /fluxos (Guid +
// Number), e a lista de conversas monitoradas é um snapshot já classificado pelo backend
// (Estourado/EmRisco/DentroDoPrazo) — não são mais recalculadas no cliente a partir do
// feed bruto de conversas do Chatwoot.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

export interface CpSlaProfile {
  id: string
  name: string
  firstResponseSeconds: number
  resolutionSeconds: number
  clientIdleSeconds: number
  businessHoursOnly: boolean
  /** Redirecionar quando o agente deixa a 1ª resposta vencer: depois do prazo + redirectGraceSeconds, vai pro próximo agente que pode atender. */
  redirectEnabled: boolean
  redirectGraceSeconds: number
  notifyBeforeSeconds: number
}

export interface CpFlowAssignment {
  flowId: string
  flowName: string
  flowNumber: number
  slaProfileId: string | null
}

export interface CpSlaConversation {
  conversationId: string
  contactName: string
  agentName: string | null
  flowName: string | null
  phase: string
  status: 'Estourado' | 'EmRisco' | 'DentroDoPrazo'
  slaDueAt: string | null
  slaBreachedAt: string | null
}

function authHeaders(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

async function get<T>(token: string, path: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}`, { headers: authHeaders(token) })
    if (!res.ok) return fallback
    return await res.json()
  } catch {
    return fallback
  }
}

export function listSlaProfiles(token: string): Promise<CpSlaProfile[]> {
  return get(token, '/sla/perfis', [])
}

export interface SlaProfileInput {
  name: string
  firstResponseSeconds: number
  resolutionSeconds: number
  clientIdleSeconds: number
  businessHoursOnly: boolean
  /** Redirecionar quando o agente deixa a 1ª resposta vencer: depois do prazo + redirectGraceSeconds, vai pro próximo agente que pode atender. */
  redirectEnabled: boolean
  redirectGraceSeconds: number
  notifyBeforeSeconds: number
}

export async function createSlaProfile(token: string, input: SlaProfileInput): Promise<{ ok: boolean; profile?: CpSlaProfile }> {
  const res = await fetch(`${WOOTDASH_BASE}/sla/perfis`, { method: 'POST', headers: authHeaders(token, true), body: JSON.stringify(input) })
  const data = res.status !== 204 ? await res.json().catch(() => undefined) : undefined
  return { ok: res.ok, profile: data }
}

export async function updateSlaProfile(token: string, id: string, input: SlaProfileInput): Promise<{ ok: boolean; profile?: CpSlaProfile }> {
  const res = await fetch(`${WOOTDASH_BASE}/sla/perfis/${id}`, { method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify(input) })
  const data = res.status !== 204 ? await res.json().catch(() => undefined) : undefined
  return { ok: res.ok, profile: data }
}

export async function deleteSlaProfile(token: string, id: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/sla/perfis/${id}`, { method: 'DELETE', headers: authHeaders(token) })
  return res.ok
}

export function listSlaFlowAssignments(token: string): Promise<CpFlowAssignment[]> {
  return get(token, '/sla/atribuicoes', [])
}

export async function setSlaFlowAssignment(token: string, flowId: string, slaProfileId: string | null): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/sla/atribuicoes/${flowId}`, {
    method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify({ slaProfileId }),
  })
  return res.ok
}

export type SlaConversationFilterKey = 'todos' | 'estourados' | 'em-risco' | 'dentro-do-prazo'

export function listSlaConversations(token: string, filtro: SlaConversationFilterKey): Promise<CpSlaConversation[]> {
  const q = filtro === 'todos' ? '' : `?filtro=${filtro}`
  return get(token, `/sla/conversas${q}`, [])
}

export interface CpFlow { id: string; number: number; name: string; isActive: boolean }

export function listFlows(token: string): Promise<CpFlow[]> {
  return get(token, '/fluxos', [])
}

export async function createFlow(token: string, name: string, number: number): Promise<{ ok: boolean; data?: any }> {
  const res = await fetch(`${WOOTDASH_BASE}/fluxos`, { method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ name, number }) })
  const data = await res.json().catch(() => undefined)
  return { ok: res.ok, data }
}

export async function updateFlow(token: string, id: string, name: string, number: number): Promise<{ ok: boolean; data?: any }> {
  const res = await fetch(`${WOOTDASH_BASE}/fluxos/${id}`, { method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify({ name, number }) })
  const data = await res.json().catch(() => undefined)
  return { ok: res.ok, data }
}

export async function deactivateFlow(token: string, id: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/fluxos/${id}`, { method: 'DELETE', headers: authHeaders(token) })
  return res.ok
}
